// Firebase web SDK initialisation. All config flows from import.meta.env —
// .env.local locally, GitHub Actions secrets in CI. Nothing in this file
// is hand-edited; the values are injected at build time.

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, browserLocalPersistence, setPersistence } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getMessaging, isSupported, type Messaging } from 'firebase/messaging';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const app: FirebaseApp = initializeApp(firebaseConfig);

// App Check via reCAPTCHA v3. Only initialised when a site key is present —
// in local dev without a key, App Check is skipped so we can still build and run.
const recaptchaSiteKey = import.meta.env.VITE_FIREBASE_RECAPTCHA_SITE_KEY;
if (typeof window !== 'undefined' && recaptchaSiteKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export const auth = getAuth(app);
setPersistence(auth, browserLocalPersistence).catch(() => {
  // Falls back to in-memory persistence — non-fatal.
});

export const db = getFirestore(app);

// Functions region matches Firestore (europe-west2). Keep these in lockstep —
// changing one without the other breaks callable functions.
export const functions = getFunctions(app, 'europe-west2');

export async function getMessagingIfSupported(): Promise<Messaging | null> {
  if (await isSupported()) return getMessaging(app);
  return null;
}
